import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import { brand } from '../brand/brand';
import type { SupportTicket, SupportTicketSummary } from '../api/types';

const CATEGORIES = [
  { value: 'payment', label: 'A payment or refund' },
  { value: 'voucher', label: 'A voucher I bought' },
  { value: 'redemption', label: 'Something at the counter' },
  { value: 'account', label: 'My account' },
  { value: 'partner', label: 'A business on the site' },
  { value: 'other', label: 'Something else' },
] as const;

/**
 * The customer's support conversations.
 *
 * ── Why this exists at all ───────────────────────────────────────────────────
 *
 * Support was a `mailto:` link. A mailto has no record, no status, and no way for
 * somebody to see whether anyone read what they sent — so the second message is
 * always "did you get my email?", and the answer is a shrug.
 *
 * ── The one thing this page must get right ───────────────────────────────────
 *
 * Somebody arriving here is already annoyed: their voucher was refused, or their
 * money went somewhere. So the form is short, the categories are written as
 * complaints rather than as database values, and nothing asks them to look up an
 * identifier before they are allowed to speak.
 */
export function SupportTickets() {
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null);
  const [open, setOpen] = useState<SupportTicket | null>(null);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    try {
      setTickets(await api.tickets());
      setError(null);
    } catch {
      setError('Could not load your messages just now.');
    }
  }

  async function openTicket(id: string) {
    try {
      // Opening marks it read on the server — the customer is looking at it.
      setOpen(await api.ticket(id));
      void load();
    } catch {
      setError('Could not open that conversation.');
    }
  }

  if (open !== null) {
    return (
      <Thread
        ticket={open}
        onBack={() => { setOpen(null); void load(); }}
        onChanged={setOpen}
      />
    );
  }

  if (composing) {
    return (
      <NewTicket
        onCancel={() => setComposing(false)}
        onCreated={(t) => { setComposing(false); setOpen(t); void load(); }}
      />
    );
  }

  return (
    <div className="page narrow stack">
      <header className="stack gap-xs">
        <h1>Help</h1>
        <p className="muted">
          Send us a message and a person will reply. Everything you send stays here,
          so you can see what was said and whether we have answered.
        </p>
      </header>

      <button type="button" className="btn primary" onClick={() => setComposing(true)}>
        Ask for help
      </button>

      {error !== null && <p className="notice error" role="alert">{error}</p>}

      {tickets === null ? (
        <p className="muted">Loading…</p>
      ) : tickets.length === 0 ? (
        <p className="muted">
          You have not asked us anything yet. If something has gone wrong, the button
          above is the fastest way to reach a person.
        </p>
      ) : (
        <ul className="ticket-list">
          {tickets.map((t) => (
            <li key={t.id}>
              <button type="button" className="ticket-row" onClick={() => void openTicket(t.id)}>
                <span className="ticket-main">
                  <strong>{t.subject}</strong>
                  <span className="faint">
                    {t.reference}
                    {t.businessName !== null && ` · ${t.businessName}`}
                    {' · '}
                    {new Date(t.updatedAt).toLocaleDateString()}
                  </span>
                </span>

                <span className="ticket-state">
                  {/* The badge people actually look for. Derived from when THEY
                      last opened it — an agent reading the ticket never clears it. */}
                  {t.hasUnread && <span className="badge">New reply</span>}
                  <span className="faint">{statusWord(t.status)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="faint">
        <Link to="/support">Other ways to reach us</Link>
      </p>
    </div>
  );
}

function NewTicket({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (t: SupportTicket) => void;
}) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<string>('other');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      onCreated(await api.createTicket({ subject: subject.trim(), category, body: body.trim() }));
    } catch (ex) {
      setError(ex instanceof ApiRequestError ? ex.message : 'Could not send that just now.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page narrow stack">
      <h1>Ask for help</h1>

      <form className="stack gap-md" onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="cat">What is it about?</label>
          <select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
            {/* Written as complaints, not as database values. Nobody arriving
                here upset should have to translate their problem into our
                vocabulary before they are allowed to speak. */}
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="subj">In a few words</label>
          <input
            id="subj"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={140}
            minLength={3}
            required
            placeholder="The counter would not take my voucher"
          />
        </div>

        <div className="field">
          <label htmlFor="body">What happened?</label>
          <textarea
            id="body"
            rows={7}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            minLength={10}
            required
            placeholder="Tell us what happened, where, and roughly when. If you have an order or voucher code, include it — it helps us find things faster."
          />
        </div>

        {error !== null && <p className="notice error" role="alert">{error}</p>}

        <div className="row gap-sm">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Thread({
  ticket,
  onBack,
  onChanged,
}: {
  ticket: SupportTicket;
  onBack: () => void;
  onChanged: (t: SupportTicket) => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finished = ticket.status === 'closed' || ticket.status === 'resolved';

  async function send() {
    if (body.trim() === '') return;

    setBusy(true);

    try {
      onChanged(await api.replyToTicket(ticket.id, body.trim()));
      setBody('');
      setError(null);
    } catch (ex) {
      setError(ex instanceof ApiRequestError ? ex.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page narrow stack">
      <button type="button" className="btn ghost self-start" onClick={onBack}>
        ← All messages
      </button>

      <header className="stack gap-xs">
        <h1>{ticket.subject}</h1>
        <p className="faint">
          {ticket.reference} · {statusWord(ticket.status)}
          {ticket.businessName !== null && ` · about ${ticket.businessName}`}
        </p>
      </header>

      <div className="thread">
        {ticket.messages.map((m) => (
          <div key={m.id} className={m.author === 'agent' ? 'msg agent' : 'msg mine'}>
            <div className="msg-head">
              {/* "Us" rather than a staff name. Which member of staff answered is
                  internal, and the API never sends it. */}
              <strong>{m.author === 'agent' ? brand.name : 'You'}</strong>
              <span className="faint">{new Date(m.createdAt).toLocaleString()}</span>
            </div>
            <p>{m.body}</p>
          </div>
        ))}
      </div>

      {finished ? (
        // Not a dead end: replying reopens it. A customer whose problem came back
        // should not have to start a new conversation and re-explain it.
        <p className="muted">
          We have marked this as answered. If it is still not right, write below and
          it comes back to us.
        </p>
      ) : null}

      <div className="stack gap-sm">
        <textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
          placeholder="Add to this conversation…"
          aria-label="Your reply"
        />

        {error !== null && <p className="notice error" role="alert">{error}</p>}

        <button
          type="button"
          className="btn primary"
          disabled={busy || body.trim() === ''}
          onClick={() => void send()}
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

/**
 * Status in words a customer recognises.
 *
 * "pending" means "we have replied and are waiting on you", which reads to a
 * customer as "nothing is happening" — the opposite of the truth.
 */
function statusWord(status: string): string {
  switch (status) {
    case 'open': return 'With us';
    case 'pending': return 'We have replied';
    case 'resolved': return 'Answered';
    case 'closed': return 'Closed';
    default: return status;
  }
}
